import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Modal,
  ModalClose,
  ModalDialog,
  IconButton,
  LinearProgress,
  Stack,
  Input,
  Textarea,
  Tooltip,
  Typography,
  FormControl,
  FormLabel,
  Switch,
  Avatar,
} from '@mui/joy';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import PollIcon from '@mui/icons-material/Poll';
import SendIcon from '@mui/icons-material/Send';
import PushPinIcon from '@mui/icons-material/PushPin';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import dayjs, { Dayjs } from 'dayjs';
import type { CongregationBatchPostInput, CongregationMember } from '../../types/congregation';

// Helper to build backend URL (similar to api/index.ts)
const buildBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

// Normalize content so blank lines render with full height in preview
const normalizeContentForPreview = (text: string) =>
  text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => (line.trim() === '' ? '\u00A0' : line))
    .join('\n');

interface BatchPostComposerProps {
  currentMember?: CongregationMember | null;
  canPin?: boolean;
  onSubmit: (post: CongregationBatchPostInput | string, isPinned?: boolean, attachments?: Attachment[]) => Promise<void>;
  loading?: boolean;
  draftKey?: string | null;
  initialContent?: string;
  initialPinned?: boolean;
  initialAttachments?: Attachment[];
  allowAttachments?: boolean;
  allowPoll?: boolean;
  onCancel?: () => void;
  submitLabel?: string;
  isEditing?: boolean;
}

interface Attachment {
  id: string;
  attachmentType: 'image' | 'file' | 'link';
  url: string;
  filename?: string;
  mimeType?: string;
}

interface PollOption {
  id: string;
  text: string;
}

export default function BatchPostComposer({
  currentMember = null,
  canPin = false,
  onSubmit,
  loading = false,
  draftKey = 'batch-post-draft',
  initialContent = '',
  initialPinned = false,
  initialAttachments = [],
  allowAttachments = true,
  allowPoll = true,
  onCancel,
  submitLabel = 'Post',
  isEditing = false,
}: BatchPostComposerProps) {
  const DRAFT_KEY = draftKey;
  
  const [content, setContent] = React.useState(initialContent);
  const [attachments, setAttachments] = React.useState<Attachment[]>(initialAttachments);
  const [pollQuestion, setPollQuestion] = React.useState('');
  const [pollOptions, setPollOptions] = React.useState<PollOption[]>([
    { id: '1', text: '' },
    { id: '2', text: '' },
  ]);
  const [pollExpiresAt, setPollExpiresAt] = React.useState<Dayjs | null>(null);
  const [isPinned, setIsPinned] = React.useState(initialPinned);
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkName, setLinkName] = React.useState('');
  const [pollDialogOpen, setPollDialogOpen] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const textFieldRef = React.useRef<HTMLTextAreaElement>(null);

  // Load draft on mount
  React.useEffect(() => {
    if (!DRAFT_KEY) return;
    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.content) setContent(parsed.content);
        if (parsed.attachments) setAttachments(parsed.attachments);
        if (parsed.pollQuestion) setPollQuestion(parsed.pollQuestion);
        if (parsed.pollOptions) setPollOptions(parsed.pollOptions);
        if (parsed.pollExpiresAt) setPollExpiresAt(dayjs(parsed.pollExpiresAt));
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
  }, [DRAFT_KEY]);

  // Save draft on content change
  React.useEffect(() => {
    if (!DRAFT_KEY) return;
    if (content || attachments.length > 0 || pollQuestion) {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ content, attachments, pollQuestion, pollOptions, pollExpiresAt })
        );
      } catch (error) {
        console.error('Failed to save draft:', error);
      }
    }
  }, [content, attachments, pollQuestion, pollOptions, pollExpiresAt, DRAFT_KEY]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPDF = file.type === 'application/pdf';
    const isImage = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type);
    const isDoc = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'application/rtf'
    ].includes(file.type);

    // Validate file type
    if (!isImage && !isPDF && !isDoc) {
      alert('Invalid file type. Supported formats: Images (JPG, PNG, WebP, GIF), Documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX), and Text files (TXT, CSV, RTF).');
      return;
    }

    // Check attachment limits
    const currentImages = attachments.filter(att => att.attachmentType === 'image').length;
    const currentDocs = attachments.filter(att => att.attachmentType === 'file').length;

    if (isImage && currentImages >= 10) {
      alert('Maximum 10 images per post. Please remove some images before adding more.');
      return;
    }

    if ((isPDF || isDoc) && currentDocs >= 5) {
      alert('Maximum 5 documents per post. Please remove some documents before adding more.');
      return;
    }

    // Validate file size (20MB limit for docs, 5MB for images)
    const maxSize = (isPDF || isDoc) ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size exceeds ${(isPDF || isDoc) ? '20MB' : '5MB'} limit`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', buildBackendUrl('/api/congregation/admin/upload-batch-post-image'));
      xhr.withCredentials = true;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      const uploadPromise = new Promise<{ url: string }>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              reject(err);
            }
          } else {
            reject(new Error('Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
      });

      const data = await uploadPromise;
      const isImage = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type);
      const newAttachment: Attachment = {
        id: Date.now().toString(),
        attachmentType: isImage ? 'image' : 'file',
        url: data.url,
        filename: file.name,
        mimeType: file.type,
      };
      setAttachments((prev) => [...prev, newAttachment]);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  // Formatting functions
  const insertMarkdown = (before: string, after: string = '', placeholder: string = 'text') => {
    const textarea = textFieldRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    const beforeText = content.substring(0, start);
    const afterText = content.substring(end);

    const newText = selected
      ? `${beforeText}${before}${selected}${after}${afterText}`
      : `${beforeText}${before}${placeholder}${after}${afterText}`;

    setContent(newText);

    // Set cursor position after insertion
    setTimeout(() => {
      const newPosition = selected
        ? start + before.length + selected.length + after.length
        : start + before.length + placeholder.length;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleBold = () => insertMarkdown('**', '**', 'bold text');
  const handleItalic = () => insertMarkdown('_', '_', 'italic text');
  const handleCode = () => insertMarkdown('`', '`', 'code');
  const handleLink = () => insertMarkdown('[', '](url)', 'link text');
  const handleUnorderedList = () => insertMarkdown('\n- ', '', 'list item');
  const handleOrderedList = () => insertMarkdown('\n1. ', '', 'list item');

  const handleAddLink = () => {
    if (!linkUrl.trim()) return;

    const newAttachment: Attachment = {
      id: Date.now().toString(),
      attachmentType: 'link',
      url: linkUrl,
      filename: linkName || linkUrl,
    };
    setAttachments((prev) => [...prev, newAttachment]);
    setLinkUrl('');
    setLinkName('');
    setLinkDialogOpen(false);
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const handleAddPollOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions((prev) => [
        ...prev,
        { id: Date.now().toString(), text: '' },
      ]);
    }
  };

  const handleRemovePollOption = (id: string) => {
    if (pollOptions.length > 2) {
      setPollOptions((prev) => prev.filter((opt) => opt.id !== id));
    }
  };

  const handlePollOptionChange = (id: string, text: string) => {
    setPollOptions((prev) =>
      prev.map((opt) => (opt.id === id ? { ...opt, text } : opt))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    // For editing, pass content, isPinned, and attachments
    if (isEditing) {
      try {
        await onSubmit(content.trim(), isPinned, attachments);
        // Don't clear draft on edit mode since we handle it in parent
      } catch (error) {
        console.error('Failed to edit post:', error);
      }
      return;
    }

    // For compose mode, build full post data
    const postData: CongregationBatchPostInput = {
      content: content.trim(),
      isPinned,
    };

    if (allowAttachments && attachments.length > 0) {
      postData.attachments = attachments.map((att) => ({
        attachmentType: att.attachmentType,
        url: att.url,
        filename: att.filename,
      }));
    }

    // Add poll if poll question and at least 2 options
    if (
      allowPoll &&
      pollQuestion.trim() &&
      pollOptions.filter((opt) => opt.text.trim()).length >= 2
    ) {
      postData.poll = {
        question: pollQuestion.trim(),
        options: pollOptions
          .filter((opt) => opt.text.trim())
          .map((opt) => opt.text.trim()),
        expiresAt: pollExpiresAt ? pollExpiresAt.toISOString() : undefined,
      };
    }

    try {
      await onSubmit(postData);
      setContent('');
      if (allowAttachments) setAttachments([]);
      if (allowPoll) {
        setPollQuestion('');
        setPollOptions([
          { id: '1', text: '' },
          { id: '2', text: '' },
        ]);
        setPollExpiresAt(null);
      }
      setIsPinned(initialPinned);
      // Clear draft
      if (DRAFT_KEY) localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Failed to create post:', error);
    }
  };

  const hasValidPoll = pollQuestion.trim() && pollOptions.filter((opt) => opt.text.trim()).length >= 2;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* Author */}
            {currentMember && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar 
                  size="sm" 
                  src={currentMember.photoUrl || undefined}
                  sx={{ bgcolor: 'primary.softBg', color: 'primary.solidColor' }}
                >
                  {currentMember.displayName?.charAt(0).toUpperCase() || '🙂'}
                </Avatar>
                <Typography level="body-sm" fontWeight="bold">
                  {currentMember.displayName}
                </Typography>
              </Stack>
            )}

          {/* Content with Live Preview */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: { xs: 'auto', md: '200px' } }}>
              {/* Editor */}
              <Box sx={{ flex: 1 }}>
                <Typography component="label" level="body-sm" sx={{ display: 'block', mb: 1 }}>
                  Compose your message
                </Typography>
                <Textarea
                  minRows={3}
                  maxRows={6}
                  slotProps={{
                    textarea: {
                      ref: textFieldRef,
                      'aria-label': 'Post content (Markdown supported)',
                    }
                  }}
                  placeholder="Share something with your batch..."
                  value={content}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit(e as any);
                    }
                  }}
                  size="sm"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: { xs: '13px', md: '14px' },
                    },
                  }}
                />
              </Box>

              {/* Live Preview - Hidden on mobile */}
              {content.trim() && (
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
                  <Typography level="body-xs" color="neutral" display="block" mb={0.5}>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      minHeight: '140px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      overflow: 'auto',
                      '& h1, & h2, & h3': { mt: 1, mb: 0.5, fontWeight: 700 },
                      '& p': { my: 0.5 },
                      '& ul, & ol': { pl: 3, my: 0.5 },
                      '& code': { px: 0.5, py: 0.25, borderRadius: '4px', bgcolor: 'action.hover', fontFamily: 'monospace' },
                      '& a': { color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' },
                      '& blockquote': { borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, ml: 0, my: 0.5 },
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      components={{
                        p: ({ children }) => <p style={{ margin: '0.5em 0' }}>{children}</p>,
                      }}
                    >
                      {normalizeContentForPreview(content)}
                    </ReactMarkdown>
                  </Box>
                </Box>
              )}
            </Stack>

            {/* Formatting Help */}
            <Typography level="body-xs" color="neutral" sx={{ display: 'block' }}>
              Use toolbar buttons or markdown syntax for formatting
            </Typography>

            {/* Attachments Preview */}
            {allowAttachments && attachments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {attachments.map((att) => (
                  <Box
                    key={att.id}
                    sx={{
                      position: 'relative',
                      width: att.attachmentType === 'image' ? 120 : 'auto',
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    {att.attachmentType === 'image' ? (
                      <Box
                        component="img"
                        src={att.url}
                        alt={att.filename}
                        loading="lazy"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 100,
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Typography level="body-xs">
                        {att.filename || att.url}
                      </Typography>
                    )}
                    <Tooltip title="Remove">
                      <IconButton
                        size="sm"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                        }}
                        onClick={() => handleRemoveAttachment(att.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}

            {allowAttachments && isUploading && (
              <Stack spacing={0.5}>
                <Typography level="body-xs" color="neutral">
                  Uploading image...
                </Typography>
                <LinearProgress value={uploadProgress || 0} />
              </Stack>
            )}

            {/* Poll Preview */}
            {allowPoll && hasValidPoll && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography level="body-sm" fontWeight="bold" mb={1}>
                  {pollQuestion}
                </Typography>
                <Stack spacing={0.75}>
                  {pollOptions
                    .filter((opt) => opt.text.trim())
                    .map((opt) => (
                      <Button
                        key={opt.id}
                        variant="outlined"
                        fullWidth
                        disabled
                        sx={{ justifyContent: 'space-between' }}
                      >
                        <Typography level="body-sm">
                          {opt.text}
                        </Typography>
                        <Typography level="body-xs">
                          0 votes
                        </Typography>
                      </Button>
                    ))}
                </Stack>
              </Box>
            )}

            {/* Toolbar */}
            <Stack spacing={{ xs: 1.5, sm: 1 }} sx={{ gap: 1 }}>
              {/* Main Formatting Row */}
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                {/* Formatting Buttons */}
                <Tooltip title="Bold (Ctrl+B)">
                  <IconButton
                    size="sm"
                    onClick={handleBold}
                    disabled={loading}
                    aria-label="Make text bold"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <FormatBoldIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Italic (Ctrl+I)">
                  <IconButton
                    size="sm"
                    onClick={handleItalic}
                    disabled={loading}
                    aria-label="Make text italic"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <FormatItalicIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Code">
                  <IconButton
                    size="sm"
                    onClick={handleCode}
                    disabled={loading}
                    aria-label="Insert code"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <CodeIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Link">
                  <IconButton
                    size="sm"
                    onClick={handleLink}
                    disabled={loading}
                    aria-label="Insert link"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <LinkIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Bullet List">
                  <IconButton
                    size="sm"
                    onClick={handleUnorderedList}
                    disabled={loading}
                    aria-label="Insert bullet list"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <FormatListBulletedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Numbered List">
                  <IconButton
                    size="sm"
                    onClick={handleOrderedList}
                    disabled={loading}
                    aria-label="Insert numbered list"
                    sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                  >
                    <FormatListNumberedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              {/* Attachments & Options Row */}
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                {/* Image Upload */}
                {allowAttachments && (
                  <>
                    <Tooltip title="Add file">
                      <IconButton
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || isUploading}
                        aria-label="Add file"
                        sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                      >
                        <ImageIcon />
                      </IconButton>
                    </Tooltip>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf"
                      hidden
                      onChange={handleImageUpload}
                      disabled={loading}
                      aria-label="Upload file"
                    />

                    {/* Link Attachment */}
                    <Tooltip title="Add link">
                      <IconButton
                        size="sm"
                        onClick={() => setLinkDialogOpen(true)}
                        disabled={loading}
                        aria-label="Add link attachment"
                        sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                      >
                        <LinkIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {/* Poll */}
                {allowPoll && (
                  <Tooltip title="Add poll">
                    <IconButton
                      size="sm"
                      onClick={() => setPollDialogOpen(true)}
                      disabled={loading}
                      aria-label="Create poll"
                      sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                    >
                      <PollIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Pin Toggle */}
                {canPin && (
                  <Tooltip title={isPinned ? 'Unpin' : 'Pin this post'}>
                    <IconButton
                      size="sm"
                      color={isPinned ? 'primary' : 'neutral'}
                      onClick={() => setIsPinned(!isPinned)}
                      disabled={loading}
                      aria-label={isPinned ? 'Unpin post' : 'Pin post'}
                      sx={{ fontSize: { xs: '18px', sm: '20px' } }}
                    >
                      <PushPinIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {/* Action Buttons - Full width on mobile */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                {onCancel && (
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={loading}
                    fullWidth={true}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Cancel
                  </Button>
                )}

                <Button
                  variant="solid"
                  endDecorator={isEditing ? undefined : <SendIcon />}
                  type="submit"
                  disabled={!content.trim() || loading}
                  fullWidth={true}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {loading ? `${isEditing ? 'Saving' : 'Posting'}...` : submitLabel}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </CardContent>

      {/* Link Modal */}
      {allowAttachments && (
        <Modal open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <ModalClose variant="plain" />
            <Typography component="h2" id="modal-title" level="h4" textColor="inherit" fontWeight="lg">
              Add Link
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>URL</FormLabel>
                <Input
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Display Name (optional)</FormLabel>
                <Input
                  placeholder="Link name"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  size="sm"
                />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleAddLink}
                  variant="solid"
                  disabled={!linkUrl.trim()}
                >
                  Add Link
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      )}

      {/* Poll Modal */}
      {allowPoll && (
        <Modal open={pollDialogOpen} onClose={() => setPollDialogOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 'sm' }}>
            <ModalClose variant="plain" />
            <Typography component="h2" id="modal-title" level="h4" textColor="inherit" fontWeight="lg">
              Create Poll
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>Poll Question</FormLabel>
                <Input
                  placeholder="What would you like to ask?"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  size="sm"
                />
              </FormControl>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography level="body-sm" fontWeight={600}>Options</Typography>
                  <Button
                    size="sm"
                    onClick={handleAddPollOption}
                    disabled={pollOptions.length >= 5}
                    startDecorator={<AddIcon />}
                  >
                    Add Option
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {pollOptions.map((option) => (
                    <Stack key={option.id} direction="row" spacing={1} alignItems="center">
                      <Input
                        size="sm"
                        placeholder={`Option ${pollOptions.indexOf(option) + 1}`}
                        value={option.text}
                        onChange={(e) => handlePollOptionChange(option.id, e.target.value)}
                      />
                      {pollOptions.length > 2 && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => handleRemovePollOption(option.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Poll closes at (optional)"
                  value={pollExpiresAt}
                  onChange={(val) => setPollExpiresAt(val)}
                />
              </LocalizationProvider>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setPollDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => setPollDialogOpen(false)}
                  variant="solid"
                  disabled={!pollQuestion.trim() || pollOptions.filter((opt) => opt.text.trim()).length < 2}
                >
                  Add Poll
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      )}
    </Card>
  );
}
